import { useContext, useEffect, useState } from 'react';
import { Grid, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFormTextarea,
    SttFormPeriod,
    SttLabelForm,
    SttFormPrice,
    SttButtonSave,
    SttList,
} from '../../sporttia/all';
import { fullName, getErrorMessage, parsePrice } from '../../lib/utils';
import DiscountList from '../discounts/DiscountsList';
import constants from '../../config/constants';
import usePaymentsService from '../../services/PaymentsService';
import type { Payment } from '../../types/models/Payment';
import DateBuilder from '../../lib/DateBuilder';
import { usePermission } from '../../lib/usePermission';
import { PERMISSIONS } from '../../lib/permissions';

type PaymentModalDetailProps = {
    payment: Payment;
    onSave?: (payment: Payment) => void;
};

export default function PaymentModalDetail({
    payment,
    onSave,
}: PaymentModalDetailProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    // Guardar el pago exige, además del privilegio `modify` del backend,
    // `accounting.modify` (siempre `true` con los roles apagados).
    const canModifyPayment = usePermission().can(PERMISSIONS.ACCOUNTING.MODIFY);
    const [paymentForm, setPaymentForm] = useState(payment);

    // Resincroniza el state local cuando el pago cargado cambia (ej:
    // el usuario abre el padre desde el banner del hijo). Sin esto,
    // `useState(payment)` retiene el valor inicial y los campos del
    // tab (precio, concepto, fechas) siguen mostrando datos del hijo.
    useEffect(() => {
        setPaymentForm(payment);
    }, [payment.id]);

    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (!name) return;
        setPaymentForm((prev) => ({ ...prev, [name]: value }));
    };

    const paymentsService = usePaymentsService();
    const updatePaymentMutation = paymentsService.useUpdatePayment({
        onSuccess: (response: { payment?: Payment }) => {
            if (response.payment) {
                onSave?.(response.payment);
                cxt.showMessage('S', t('generic.saved'));
            }
        },
        onError: (error) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    return (
        <Box mt={2}>
            <Grid container spacing={3}>
                <SttLabelForm
                    grid
                    title={t('generic.created')}
                    md={3}
                    text={new DateBuilder(
                        paymentForm.created,
                        cxt?.sc?.timezone?.name,
                        cxt?.lang?.key,
                    ).dmyhm()}
                />

                <SttLabelForm
                    grid
                    title={t('generic.user')}
                    md={4}
                    text={
                        paymentForm.user
                            ? `@${paymentForm.user.login} - ${fullName(
                                  paymentForm.user,
                              )}`
                            : paymentForm.name
                    }
                />

                {paymentForm.paymentForm && (
                    <SttLabelForm
                        grid
                        title={t('payment.paymentForm')}
                        md={2}
                        text={t(`PF.${paymentForm.paymentForm}`)}
                    />
                )}

                {paymentForm.collector && (
                    <SttLabelForm
                        grid
                        title={t('generic.collector')}
                        md={2}
                        text={`@${paymentForm.collector.login} - ${paymentForm.collector.name}`}
                    />
                )}

                <SttFormTextarea
                    grid
                    xs={12}
                    md={12}
                    disabled={
                        paymentForm.status ===
                        constants.payment.status.paid.name
                    }
                    name="concept"
                    caption={t('generic.concept')}
                    defVal={paymentForm.concept || ''}
                    onChange={handleForm}
                />

                <SttFormPrice
                    grid
                    xs={12}
                    sm={6}
                    md={3}
                    disabled={
                        paymentForm.status ===
                        constants.payment.status.paid.name
                    }
                    name="price"
                    caption={t('generic.price')}
                    defVal={String(paymentForm.price)}
                    onChange={handleForm}
                />

                <SttFormPeriod
                    grid
                    xs={12}
                    sm={12}
                    md={9}
                    disabled
                    caption={t('generic.period')}
                    nameIni="ini"
                    nameEnd="end"
                    defValIni={paymentForm.ini}
                    defValEnd={paymentForm.end}
                    onChange={handleForm}
                />
            </Grid>

            {paymentForm.receipts && (
                <Box my={3}>
                    <SttList
                        title={t('generic.receipts')}
                        data={paymentForm.receipts.map((rpt) => ({
                            caption: rpt.remittence.name,
                        }))}
                    />
                </Box>
            )}

            {!!paymentForm.discounts?.length && (
                <Box my={3}>
                    <DiscountList
                        discounts={paymentForm.discounts}
                        showEmptyMessage={false}
                    />
                </Box>
            )}

            <Box mt={2}>
                <Grid container spacing={3}>
                    {canModifyPayment &&
                        paymentForm.privileges?.includes(
                            constants.payment.privileges.modify,
                        ) && (
                            <Grid size={{ md: 3, xs: 12 }}>
                                <SttButtonSave
                                    loading={updatePaymentMutation.isPending}
                                    onClick={() => {
                                        updatePaymentMutation.mutate({
                                            id: payment.id,
                                            params: {
                                                concept:
                                                    paymentForm.concept || '',
                                                price:
                                                    parsePrice(
                                                        paymentForm.price ||
                                                            '0',
                                                    ) || '0',
                                            },
                                        });
                                    }}
                                />
                            </Grid>
                        )}
                </Grid>
            </Box>
        </Box>
    );
}
